import { useState, useCallback } from 'react';
import { Upload, X, FileText, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { DocumentCategory } from '@/types/client';
import { DOCUMENT_CATEGORY_LABELS, formatFileSize } from '@/types/client';
import apiClient from '@/services/api/client';

interface UploadFile {
  file: File;
  id: string;
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'success' | 'error';
  error?: string;
  fileId?: string;
}

interface DocumentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  clientName: string;
  folderId?: string;
  folderPath?: string;
  onUploadComplete?: () => void;
}

export function DocumentUploadDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  folderId,
  folderPath,
  onUploadComplete,
}: DocumentUploadDialogProps) {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [category, setCategory] = useState<DocumentCategory>('other');
  const [description, setDescription] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
      e.target.value = '';
    }
  };

  const addFiles = (newFiles: File[]) => {
    const uploadFiles: UploadFile[] = newFiles.map((file) => ({
      file,
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      progress: 0,
      status: 'pending' as const,
    }));
    setFiles((prev) => [...prev, ...uploadFiles]);
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const updateFile = (id: string, updates: Partial<UploadFile>) => {
    setFiles((prev) => prev.map((f) => f.id === id ? { ...f, ...updates } : f));
  };

  const uploadSingleFile = async (uploadFile: UploadFile) => {
    try {
      updateFile(uploadFile.id, { status: 'uploading', progress: 10 });

      // 1. Get presigned upload URL
      const contentType = uploadFile.file.type || 'application/octet-stream';
      const urlResponse = await apiClient.post(`/clients/${clientId}/files/upload-url`, {
        filename: uploadFile.file.name,
        contentType,
        fileSizeBytes: uploadFile.file.size,
        folderId: folderId || null,
      });
      const { uploadUrl, fileId } = urlResponse.data.data;
      updateFile(uploadFile.id, { progress: 30, fileId });

      // 2. Upload file to S3 via presigned URL
      await fetch(uploadUrl, {
        method: 'PUT',
        body: uploadFile.file,
        headers: { 'Content-Type': contentType },
      });
      updateFile(uploadFile.id, { progress: 70, status: 'processing' });

      // 3. Confirm upload and trigger processing
      await apiClient.post(`/clients/${clientId}/files/${fileId}/confirm`, {
        description,
        category: category !== 'other' ? category : undefined,
      });
      updateFile(uploadFile.id, { progress: 100, status: 'success' });

    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err.message || 'Upload failed';
      updateFile(uploadFile.id, { status: 'error', error: errorMsg });
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setIsUploading(true);

    // Upload files sequentially to avoid overwhelming the API
    for (const file of files) {
      if (file.status === 'pending') {
        await uploadSingleFile(file);
      }
    }

    setIsUploading(false);

    if (onUploadComplete) {
      onUploadComplete();
    }

    // Close after a brief delay if all succeeded
    const allSuccess = files.every(f => f.status === 'success');
    if (allSuccess) {
      setTimeout(() => {
        setFiles([]);
        setCategory('other');
        setDescription('');
        onOpenChange(false);
      }, 1500);
    }
  };

  const allUploaded = files.length > 0 && files.every((f) => f.status === 'success');
  const hasErrors = files.some((f) => f.status === 'error');
  const completedCount = files.filter(f => f.status === 'success').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isUploading) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Upload Documents</DialogTitle>
          <DialogDescription>
            Upload documents for <strong>{clientName}</strong>
            {folderPath && <> to <strong>{folderPath}</strong></>}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Drag & Drop Area */}
          {files.length === 0 && (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`
                border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer
                ${isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
              `}
              onClick={() => document.getElementById('file-upload')?.click()}
            >
              <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Drop files here</h3>
              <p className="text-sm text-muted-foreground mb-4">
                or click to browse your computer
              </p>
              <input
                id="file-upload"
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt,.csv"
              />
              <p className="text-xs text-muted-foreground mt-4">
                PDF, Word, Excel, CSV, Images, Text (max 50MB each)
              </p>
            </div>
          )}

          {/* File List */}
          {files.length > 0 && (
            <>
              <ScrollArea className="flex-1 border rounded-lg p-4" style={{ maxHeight: '300px' }}>
                <div className="space-y-3">
                  {files.map((uploadFile) => (
                    <div key={uploadFile.id} className="flex items-start gap-3 p-3 rounded-lg border bg-card">
                      <div className="mt-1">
                        {uploadFile.status === 'success' ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        ) : uploadFile.status === 'error' ? (
                          <AlertCircle className="h-5 w-5 text-red-500" />
                        ) : uploadFile.status === 'uploading' || uploadFile.status === 'processing' ? (
                          <Loader2 className="h-5 w-5 text-primary animate-spin" />
                        ) : (
                          <FileText className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-sm font-medium truncate">{uploadFile.file.name}</p>
                          {uploadFile.status === 'pending' && !isUploading && (
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeFile(uploadFile.id)}>
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                          <span>{formatFileSize(uploadFile.file.size)}</span>
                          {uploadFile.status === 'processing' && <span className="text-primary">Processing...</span>}
                          {uploadFile.status === 'success' && <span className="text-green-500">Complete</span>}
                        </div>
                        {(uploadFile.status === 'uploading' || uploadFile.status === 'processing' || uploadFile.status === 'success') && (
                          <Progress value={uploadFile.progress} className="h-1" />
                        )}
                        {uploadFile.error && (
                          <p className="text-xs text-red-500 mt-1">{uploadFile.error}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {!isUploading && !allUploaded && (
                <Button type="button" variant="outline" className="w-full" onClick={() => document.getElementById('file-upload')?.click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  Add More Files
                </Button>
              )}

              {/* Metadata Fields */}
              {!allUploaded && !isUploading && (
                <div className="space-y-3 pt-2 border-t">
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select value={category} onValueChange={(v) => setCategory(v as DocumentCategory)}>
                      <SelectTrigger id="category" className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(DOCUMENT_CATEGORY_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="description">Description (Optional)</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Add notes about these documents..."
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="text-sm text-muted-foreground">
            {files.length > 0 && (
              <>
                {completedCount > 0 && <span className="text-green-500">{completedCount} uploaded</span>}
                {errorCount > 0 && <span className="text-red-500 ml-2">{errorCount} failed</span>}
                {completedCount === 0 && errorCount === 0 && <span>{files.length} file{files.length !== 1 ? 's' : ''} selected</span>}
                {allUploaded && <Badge variant="default" className="ml-2">All Complete</Badge>}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUploading}>
              {allUploaded ? 'Close' : 'Cancel'}
            </Button>
            {!allUploaded && (
              <Button onClick={handleUpload} disabled={files.length === 0 || isUploading}>
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading {completedCount + 1}/{files.length}...
                  </>
                ) : (
                  `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
